/*
 * One browser, several tabs, one session cookie: what one tab learns about the session, the others
 * need to know for their hub connections.
 *
 * - The session ENDED (sign-out, or the login page after an expiry): every other tab's connection
 *   then belongs to a session that is over, and a forgotten tab would keep that session's lock lease
 *   alive: the API releases a session's record locks only once its last hub connection is gone, and
 *   its logout releases none. The login page announces it and every connected tab closes its
 *   connection (NotificationsProvider).
 * - A tab CONNECTED: a session cookie works again, so a tab that stopped on a 401 (signed out)
 *   starts again, under whatever session the cookie now holds.
 */

const CHANNEL_NAME = 'miconnect.session';
const SESSION_ENDED = 'session-ended';
const SESSION_CONNECTED = 'session-connected';

type SessionMessage = typeof SESSION_ENDED | typeof SESSION_CONNECTED;

function openChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;

  try {
    return new BroadcastChannel(CHANNEL_NAME);
  } catch {
    return null;
  }
}

function announce(message: SessionMessage): void {
  const channel = openChannel();
  if (!channel) return;

  try {
    channel.postMessage(message);
  } catch {
    // The other tabs only lose a hint: their next negotiate meets the cookie as it is anyway.
  } finally {
    channel.close();
  }
}

function listen(message: SessionMessage, listener: () => void): () => void {
  const channel = openChannel();
  if (!channel) return () => undefined;

  channel.onmessage = (event: MessageEvent<unknown>) => {
    if (event.data === message) listener();
  };

  return () => channel.close();
}

/** Tells every other tab of this browser that the session is over. Best effort; never throws. */
export function announceSessionEnded(): void {
  announce(SESSION_ENDED);
}

/** Calls `listener` whenever another tab announces the end of the session. Returns the
 *  unsubscribe; a no-op where BroadcastChannel is missing. */
export function onSessionEnded(listener: () => void): () => void {
  return listen(SESSION_ENDED, listener);
}

/** Tells every other tab of this browser that a hub connection just opened. Best effort. */
export function announceSessionConnected(): void {
  announce(SESSION_CONNECTED);
}

/** Calls `listener` whenever another tab announces a new hub connection. Returns the unsubscribe. */
export function onSessionConnected(listener: () => void): () => void {
  return listen(SESSION_CONNECTED, listener);
}
